import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { LoginResponse } from '../../../shared/models/loginresponse.model';
import { AuthUser } from '../../../shared/models/authuser.model';
import { RolModel } from '../../../shared/models/role.model';
import { TranslateService } from '@ngx-translate/core';
import { UserModel } from '../../../shared/models/user.model';
import { TwoFactorAtuhModel, TwoFactorResendRequest, TwoFactorVerifyRequest } from '../../../shared/models/two-factor-auth.model';
import { ChatRealtimeService } from '../api/chat/chat-realtime.service';
import { TimezoneService } from '../other/timezone.service';

@Injectable({ providedIn: 'root' }) 
export class AuthService {
  private apiUrl = `${environment.apiUrl}/login`;
  private verifyCodeUrl = `${this.apiUrl}/verify`;
  private resendCodeUrl = `${this.apiUrl}/resend`;
  private verifyUrl = `${environment.apiUrl}/verify-user`; 
  private logoutUrl = `${environment.apiUrl}/logout`;
 

/**
 * Verifica si el usuario actual existe en el servidor
 * Versión simple y directa sin complejidades
 */

  verifyUserSimple(from: string): Observable<boolean> {
    //console.log('verifyUserSimple 🌍 Idioma actual:', this.translate.currentLang);
    //console.log('verifyUserSimple 🌍 Idioma fallback:', this.translate.getDefaultLang?.() || 'no definido');

    const token = this.getToken();

    if (!token) {
      this.clearAuthState();
      return of(false);
    }

    return this.http.get<any>(this.verifyUrl, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
    // Soporta ambos formatos: { user } o user plano
    map(resp => (resp && resp.user) ? resp.user : resp),
    // Normaliza al modelo (tu constructor ya hace toBool/trim/fallbacks)
    map(dto => new UserModel(dto)),

    tap(user => {
         if (user && user.is_active) {
          //  Usuario existe: Crear reducedUser igual que en login
          const reducedUser: AuthUser = {
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            profile_picture: user.profile_picture || null,
            language: user.language || null,
            agency: user.agency || null,
            timezone: user.timezone ?? null,
            timezone_mode: user.timezone_mode ?? null
          };

          //  console.log(' verifyUserSimple response.user:', reducedUser);
          // console.log(' verifyUserSimple response.user.language:', reducedUser.language);
          // console.log('verifyUserSimple response.user.language?.code:', reducedUser.language?.code);


          // Obtener el rol desde la respuesta, o desde el usuario actual si no viene
          const role = user.role || this.getUserRole();

          // Guardar en AuthService con rememberMe = true
          this.setUser(reducedUser, role, true);

          // 🔥 Aplica idioma también aquí por si venías de una sesión previa sin pasar por setUser
          const langCode = user.language?.code || this.getUserLanguage() || 'en';
          //  console.log('verifyUserSimple  va a aplicar:', langCode);
          // this.applyLanguage(langCode);

        } else {
          // ❌ Usuario no existe: limpiar estado
          this.clearAuthState();
          //  console.log(from, 'verifyUserSimple: usuario inactivo o vacío');
        }
      }),
      map(user => !!user?.id && user.is_active),
      catchError(error => {
        this.clearAuthState();
        console.error(from + ' Error verificando usuario:', error.message);
        return of(false);
      })
    );
  }














  

  // Reactive user signal
  private userSignal = signal<AuthUser | null>(this.loadUserFromStorage());
  user = this.userSignal; // ← público

  constructor(private http: HttpClient, 
    private translate: TranslateService,
    private readonly chatRealtimeService: ChatRealtimeService,
    private timezoneService: TimezoneService) {}

  // ──────── Login ────────
  // login(credentials: { username: string; password: string }): Observable<LoginResponse> {
  //   return this.http.post<LoginResponse>(this.apiUrl, credentials);
  // }

  // login(credentials: { username: string; password: string }): Observable<TwoFactorAtuhModel> {
  //   return this.http.post<TwoFactorAtuhModel>(this.apiUrl, credentials);
  // }

  login(credentials: {
      username: string;
      password: string;
    }): Observable<TwoFactorAtuhModel> {
      return this.http.post<TwoFactorAtuhModel>(
        this.apiUrl,
        credentials
      );
  }

  // verifyCode(towFactor: { username: string; code: string }): Observable<LoginResponse> {
  //   return this.http.post<LoginResponse>(this.verifyCodeUrl, towFactor);
  // }

  verifyCode(payload: TwoFactorVerifyRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      this.verifyCodeUrl,
      payload
    );
  }

  resendCode(
    payload: TwoFactorResendRequest): Observable<TwoFactorAtuhModel> {
    return this.http.post<TwoFactorAtuhModel>(
      this.resendCodeUrl,
      payload
    );
  }

  // ──────── Two-Factor State ────────
  setTwoFactorState(
    username: string,
    response: TwoFactorAtuhModel
  ): void {
    sessionStorage.setItem('two_factor_username', username);

    if (response.two_factor_token) {
      sessionStorage.setItem(
        'two_factor_token',
        response.two_factor_token
      );
    }

    if (response.resend_available_at) {
      sessionStorage.setItem(
        'two_factor_resend_available_at',
        response.resend_available_at
      );
    }

    if (response.code_expires_at) {
      sessionStorage.setItem(
        'two_factor_code_expires_at',
        response.code_expires_at
      );
    }
  }

  // ──────── Two-Factor Getters ────────
  getTwoFactorUsername(): string | null {
    return sessionStorage.getItem('two_factor_username');
  }

  getTwoFactorToken(): string | null {
    return sessionStorage.getItem('two_factor_token');
  }

  getTwoFactorResendAvailableAt(): string | null {
    return sessionStorage.getItem(
      'two_factor_resend_available_at'
    );
  }

  getTwoFactorCodeExpiresAt(): string | null {
    return sessionStorage.getItem(
      'two_factor_code_expires_at'
    );
  }

  // ──────── Clear Two-Factor State ────────
  clearTwoFactorState(): void {
    sessionStorage.removeItem('two_factor_username');
    sessionStorage.removeItem('two_factor_token');
    sessionStorage.removeItem(
      'two_factor_resend_available_at'
    );
    sessionStorage.removeItem(
      'two_factor_code_expires_at'
    );
  }

  // ──────── Token & Rol ────────
  setAuthState(token: string, role: string, remember = false): void {
    this.clearAuthState();
    const storage = remember ? localStorage : sessionStorage;
    storage.setItem('token', token);
    storage.setItem('role', role);
  }

  /**
   * Sincroniza automáticamente la zona horaria
   * del navegador con las preferencias de Nexus.
   *
   * Se ejecuta después de autenticar al usuario.
   *
   * Reglas:
   * - Manual: no modificar.
   * - Automático: actualizar si cambió la zona.
   * - NULL: inicializar como automático.
   *
   * Si la petición falla, no se interrumpe la sesión.
   */
  private syncUserTimezone(user: AuthUser): void {

    const request =
      this.timezoneService.syncAutomaticTimezone(user);

    if (!request) {
      return;
    }

    request.subscribe({
      next: (response) => {

        // Evitar actualizar el estado de otra sesión.
        if (this.userSignal()?.id !== user.id) {
          return;
        }

        this.updateTimezoneState(
          response.timezone,
          response.timezone_mode
        );
      },

      error: (error) => {
        console.warn(
          'Unable to synchronize timezone:',
          error
        );
      }
    });
  }


  setUser(user: AuthUser, role: any, remember = false): void {
    const storage = remember ? localStorage : sessionStorage;
    // console.log('claudia claudia, setUser, user='+JSON.stringify(user))
    storage.setItem('user', JSON.stringify(user));
    storage.setItem('role', JSON.stringify(role)); // <--- Guardar rol completo con políticas
    
   
    // Guarda el código de idioma para futuras sesiones
    const langCode = (user as any)?.language?.code;
      if (langCode) {
        storage.setItem('lang', langCode);
        // Aplica inmediatamente
        this.applyLanguage(langCode);
      }
  
    this.userSignal.set(user);

    // Sincronizar zona horaria después de guardar la sesión.
    this.syncUserTimezone(user);

    this.chatRealtimeService.connect();

    const rootAgencyId =
      user.agency?.parent_agency_id
      ?? user.agency?.id;

    if (rootAgencyId) {
      this.chatRealtimeService.joinPresenceChannel(
        rootAgencyId
      );
    }
  }

  private applyLanguage(langCode: string | null | undefined) {
  const lang = (langCode || this.getUserLanguage() || 'en').toLowerCase();
  // console.log('🌍 applyLanguage -> solicitando:', lang);

  this.translate.use(lang).subscribe({
    next: () => {
     // console.log(' Idioma aplicado:', this.translate.currentLang)
    },
    error: (e) => console.error('❌ Error cargando traducciones de', lang, e)
  });
  }


  isAuthenticated(): boolean {
    return !!(localStorage.getItem('token') || sessionStorage.getItem('token'));
  }

  getToken(): string | null {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  }

  getUserRole(): RolModel | null {
    const roleRaw = localStorage.getItem('role') || sessionStorage.getItem('role');
    if (!roleRaw) return null;

    try {
      const parsed = JSON.parse(roleRaw);
      return new RolModel(parsed); // 👈 convertir al modelo directamente
    } catch (e) {
      console.error('Error parseando el rol del storage', e);
      return null;
    }
  }

  getUserLanguage(): string {
    // Devuelve el código del idioma ('es', 'en', etc.)
    return localStorage.getItem('lang') || sessionStorage.getItem('lang') || 'en';
  }

  getLanguageObject(): any | null {
    const raw = localStorage.getItem('language') || sessionStorage.getItem('language');
    return raw ? JSON.parse(raw) : null;
  }

  getUser(): AuthUser | null {
    return this.userSignal();
  }


  clearAuthState(): void {
    this.chatRealtimeService.disconnect();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('role');
    this.clearTwoFactorState();
    this.userSignal.set(null);
  }

  private loadUserFromStorage(): AuthUser | null {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  }




  logout(): Observable<any> {
    const token = this.getToken();

    if (!token) {
      this.clearAuthState();
      return of(null);
    }

    return this.http.post<any>(this.logoutUrl, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap(() => this.clearAuthState()),
      catchError(() => {
        this.clearAuthState();
        return of(null);
      })
    );
  }


  updateTimezoneState(
    timezone: string,
    timezone_mode: 'automatic' | 'manual'
  ): void {

    const user = this.userSignal();

    if (!user) return;

    const updatedUser: AuthUser = {
      ...user,
      timezone,
      timezone_mode
    };

    this.userSignal.set(updatedUser);

    const storage =
      localStorage.getItem('token')
        ? localStorage
        : sessionStorage;

    storage.setItem(
      'user',
      JSON.stringify(updatedUser)
    );
  }
}
